import React from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { Formik, Form, Field } from 'formik';
import IcomoonReact from 'icomoon-react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import NoHoverButton from '../../../shared/components/NoHoverButton';
import Text from '../../../shared/_TBComponents/Text';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import { ColorGreen } from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import {
  resetSubmitSupportError,
  setReportPostId,
  submitSupport,
} from '../ducks/journalActions';

const ICON_TEXT_PROPS = {
  minWidth: '200px',
  display: 'flex',
  justifyContent: 'center',
  fontSize: '26px',
  fontWeight: '300',
  marginTop: '10px',
};

const LABEL_TEXT_PROPS = {
  marginBottom: '5px',
  fontWeight: 'bold',
};

const CloseButtonContainer = styled('button')`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: end;
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 100;
`;

const DividerLine = styled('div')`
  border-top: 2px solid rgb(232, 232, 232);
  margin-bottom: 30px;
  margin-left: -30px;
  width: calc(100% + 60px);
`;

const ErrorText = styled('div')`
  color: #ff6600;
  font-size: 13px;
  line-height: 19.5px;
`;

const FieldError = styled('div')`
  color: #ff6600;
  font-size: 14px;
`;

const FormGroup = styled('div')`
  z-index: 1;
  .form-control {
    margin-bottom: 6px;
    min-height: 30px;
    width: 100%;
  }
  .text-area {
    max-height: 250px;
    min-height: 100px;
    overflow: auto;
  }
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const ModalContentContainer = styled('div')`
  .submit-button {
    align-items: center;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    bottom: 0;
    margin-left: -30px;
    position: absolute;
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  position: default;
  width: 100%;
`;

const SupportTextWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 130px;
  margin-top: -10px;
`;

const TitlePlusErrorWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const supportSchema = Yup.object().shape({
  body: Yup.string().required('Message is required'),
});

const ReportPostModal = ({ onRequestClose }) => {
  const { reportPost, submitSupportError } = useSelector(
    (state) => state.journal.data,
  );
  const { isSupportSubmitting } = useSelector((state) => state.journal.ui);

  const theme = useTheme();
  const dispatch = useDispatch();

  const PROMPT_TEXT_PROPS = {
    fontSize: theme.textFontSizes[2],
    fontWeight: 600,
  };

  const KNOWLEDGE_BASE_TEXT_PROPS = {
    fontSize: theme.textFontSizes[1],
    marginBottom: '15px',
    marginTop: '15px',
  };

  const handleCloseModal = () => {
    onRequestClose();
    batch(() => {
      dispatch(resetSubmitSupportError());
      dispatch(setReportPostId(null));
    });
  };

  const handleSubmitSupport = (values) => {
    dispatch(submitSupport(values));
    journalTracker('Report post modal', `Report offensive content submitted - ${reportPost?.type}`);
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    setTimeout(() => {
      handleSubmitSupport(values);
      setSubmitting(false);
    });
  };

  const renderErrorSection = () => submitSupportError && (
  <ErrorText>{submitSupportError?.response?.headers?.message}</ErrorText>
  );

  const renderForm = (errors, touched, handleChange, values) => (
    <>
      <FormGroup>
        <TitlePlusErrorWrapper>
          <Text id='subject-label' styledTextProps={LABEL_TEXT_PROPS}>Subject</Text>
          {errors.subject && touched.subject && (
            <FieldError>{errors.subject}</FieldError>
          )}
        </TitlePlusErrorWrapper>
        <Field
          className='form-control'
          id='subject-field'
          name='subject'
          value={`Reporting Offensive Journal ${reportPost?.type} (ID: ${reportPost?.id})`}
          disabled
          aria-labelledby='subject-label'
        />
      </FormGroup>
      <FormGroup>
        <TitlePlusErrorWrapper>
          <Text id='message-label' styledTextProps={LABEL_TEXT_PROPS}>Message</Text>
          {errors.body && touched.body && (
            <FieldError>{errors.body}</FieldError>
          )}
        </TitlePlusErrorWrapper>
        <Field
          onChange={handleChange}
          name='body'
          component='textarea'
          className='form-control text-area'
          placeholder='Describe your problem or concern here...'
          value={values.body}
          aria-labelledby='message-label'
          aria-label='Message describing the problem or concern'
        />
      </FormGroup>
    </>
  );

  const renderSubmitButton = (handleSubmit, messageText) => (
    <NoHoverButton
      bottom
      fullWidth
      cta={!isSupportSubmitting ? 'Submit' : 'Submitting...'}
      customColor={ColorGreen}
      className='submit-button'
      large
      noBorder
      primary
      square
      disabled={isSupportSubmitting || !messageText.trim().length}
      onClick={handleSubmit}
    />
  );

  const renderSupportHeader = () => (
    <SupportTextWrapper>
      <IcomoonReact aria-hidden='true' iconSet={iconSet} size={60} icon='question-mark-circle' />
      <Text styledTextProps={ICON_TEXT_PROPS}>Support</Text>
    </SupportTextWrapper>
  );

  const renderTextSection = () => (
    <>
      <DividerLine />
      <Text styledTextProps={PROMPT_TEXT_PROPS}>
        Report innappropriate or offensive content using the form below.
      </Text>
      <Text styledTextProps={KNOWLEDGE_BASE_TEXT_PROPS}>
        Help us keep this community safe and respectful. Our team will review
        your message as soon as possible.
      </Text>
    </>
  );

  return (
    <ModalWrapper>
      <CloseButtonContainer onClick={handleCloseModal} aria-label='Close modal'>
        <IcomoonReact aria-hidden='true' iconSet={iconSet} size={10} icon='remove' />
      </CloseButtonContainer>
      <ModalContentContainer>
        <Formik
          validationSchema={supportSchema}
          initialValues={{
            subject: `Reporting Offensive Journal ${reportPost?.type} (ID: ${reportPost?.id})`,
            body: '',
            page: '/journal',
          }}
          onSubmit={handleFormSubmit}
        >
          {({
            errors, touched, handleSubmit, handleChange, values,
          }) => (
            <Form>
              {renderSupportHeader()}
              {renderTextSection()}
              {renderForm(errors, touched, handleChange, values)}
              {renderErrorSection()}
              {renderSubmitButton(handleSubmit, values.body)}
            </Form>
          )}
        </Formik>
      </ModalContentContainer>
    </ModalWrapper>
  );
};

ReportPostModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default ReportPostModal;
